import { QueryClient, QueryFunction, QueryKey } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { resolveAppMode } from 'utils/appMode';
import {
  OptionsWithMeta,
  WithLinks,
  getOptionsFirstSiteId,
  hasAccountDataInCache,
  hasSiteDataInCache,
  isOperationAvailableInCache,
  isOperationDeclaredOnItem,
} from 'utils/links';

import { getProfileRoles, profileQueryOptions } from 'api/account';

import { isAxios404Error } from 'component/hooks/use404ErrorCallback';

import type { RouterContext } from './__root';

interface BeforeLoadContext {
  context: RouterContext;
}

/**
 * Guard that blocks registration routes in WP Site Control mode.
 * Redirects to not-found page.
 */
export function blockRegistrationInWpSiteControl() {
  const { isWpSiteControl } = resolveAppMode();

  if (isWpSiteControl) {
    throw redirect({
      to: '/n/not-found',
      replace: true,
    });
  }
}

/**
 * Guard that forces the user to set a password when they don't have one yet.
 */
export async function requirePasswordSet({ context }: BeforeLoadContext) {
  const { usesAuth0 } = resolveAppMode();

  if (!usesAuth0) return;

  const profile = await context.ensureQueryData(profileQueryOptions());
  const client = profile.result?.client;

  if (client?.password_set === false) {
    throw redirect({
      to: '/set-password',
      replace: true,
    });
  }
}

/**
 * Guard that requires user to have an active plan.
 * Redirects to plan selection page if no active plan.
 */
export async function requireActivePlan({ context }: BeforeLoadContext) {
  const { isWpSiteControl } = resolveAppMode();

  if (isWpSiteControl) return;

  const profile = await context.ensureQueryData(profileQueryOptions());

  const roles = getProfileRoles(profile);

  if (roles.includes('user_with_no_accounts')) {
    throw redirect({ to: '/n/no-accounts', replace: true });
  }

  if (!profile.result?.package?.package_id) {
    throw redirect({ to: '/n/register/plan', replace: true });
  }
}

/**
 * Guard that requires user to NOT have an active plan.
 * Redirects to success page if user already has a plan.
 */
export async function requireNoPlan({ context }: BeforeLoadContext) {
  const { isWpSiteControl } = resolveAppMode();

  if (isWpSiteControl) return;

  const profile = await context.ensureQueryData(profileQueryOptions());

  const roles = getProfileRoles(profile);

  if (roles.includes('user_with_no_accounts')) {
    throw redirect({ to: '/n/no-accounts', replace: true });
  }

  if (profile.result?.package?.package_id) {
    throw redirect({ to: '/n/register/success', replace: true });
  }
}

/**
 * Guard that requires user to have an active plan OR be a user_with_no_accounts.
 * Used by settings routes that should remain accessible to no-accounts users.
 */
export async function requireActivePlanOrNoAccounts({ context }: BeforeLoadContext) {
  const { isWpSiteControl } = resolveAppMode();

  if (isWpSiteControl) return;

  const profile = await context.ensureQueryData(profileQueryOptions());

  const roles = getProfileRoles(profile);

  if (roles.includes('user_with_no_accounts')) {
    return;
  }

  if (!profile.result?.package?.package_id) {
    throw redirect({ to: '/n/register/plan', replace: true });
  }
}

/** Allowed redirect targets for unavailable operations. */
export type OperationRedirectTarget =
  | '/n/sites'
  | '/n/settings/account-and-security'
  | '/n/dashboard';

interface RequireOperationConfig<TParams extends Record<string, string>> {
  /** The route is available when any of returned operations is available. */
  readonly options: (args: { params: TParams }) => OptionsWithMeta | OptionsWithMeta[];
  /** Gets the site ID for mutations, which do not include it in their metadata. */
  readonly siteId?: (args: { params: TParams }) => string | number | undefined;
  readonly redirectTo?: OperationRedirectTarget;
}

/**
 * Requires at least one operation to be available.
 * If the cache has no data, let the component handle the request error.
 * This also prevents redirect loops after a failed site request.
 */
export function requireOperation<TParams extends Record<string, string>>(
  config: RequireOperationConfig<TParams> | ((args: { params: TParams }) => OptionsWithMeta)
) {
  const {
    options: getOptions,
    siteId: getSiteId,
    redirectTo,
  } = typeof config === 'function'
    ? ({ options: config } as RequireOperationConfig<TParams>)
    : config;

  return async function ({ context, params }: BeforeLoadContext & { params: TParams }) {
    const resolved = getOptions({ params });
    const optionsList = Array.isArray(resolved) ? resolved : [resolved];
    const siteId = getSiteId?.({ params }) ?? getOptionsFirstSiteId(optionsList);

    if (siteId != null) {
      if (!hasSiteDataInCache(context.queryClient, siteId)) return;
    } else if (!hasAccountDataInCache(context.queryClient)) {
      return;
    }

    if (isOperationAvailableInCache(context.queryClient, optionsList, siteId)) return;

    if (redirectTo) {
      throw redirect({ to: redirectTo, replace: true });
    }

    if (siteId != null) {
      throw redirect({
        to: '/n/sites/$siteId/overview',
        params: { siteId: String(siteId) },
        replace: true,
      });
    }

    throw redirect({ to: '/n/sites', replace: true });
  };
}

type OperationQuery = OptionsWithMeta & {
  readonly queryKey: QueryKey;
};

interface RequireObjectOperationConfig<TOptions extends OperationQuery> {
  readonly context: RouterContext;
  /** Detail query. Fetched only to revalidate a response that is already cached. */
  readonly options: TOptions;
  readonly redirectTo: OperationRedirectTarget;
  /** Object from a list cache. Used when the detail response is not cached. */
  readonly cachedItem?: WithLinks | null;
  /** Runs when a revalidation returns 404. May throw a redirect. */
  readonly onNotFound?: () => never;
}

/**
 * Allows a detail route from the object's own links.
 * A missing `links` array falls back to the account profile.
 * A cached detail is revalidated before that decision, and a denial removes it from the cache.
 * The detail endpoint is not requested when links already on hand deny access.
 */
export async function requireObjectOperation<TOptions extends OperationQuery>({
  context,
  options,
  redirectTo,
  cachedItem,
  onNotFound,
}: RequireObjectOperationConfig<TOptions>) {
  const { queryClient } = context;
  const detailIsCached = queryClient.getQueryData(options.queryKey) !== undefined;
  let item = cachedItem ?? undefined;

  // A list row that already omits the operation is enough. Do not request the detail.
  if (!detailIsCached && isOperationDeclaredOnItem(item, options) === false) {
    throw redirect({ to: redirectTo, replace: true });
  }

  if (detailIsCached) {
    await queryClient.invalidateQueries({ queryKey: options.queryKey, refetchType: 'none' });
    try {
      const fresh = await queryClient.fetchQuery({
        queryKey: options.queryKey,
        queryFn: (options as { queryFn?: QueryFunction }).queryFn,
        staleTime: 0,
      });
      item = (fresh as { result?: WithLinks } | undefined)?.result ?? item;
    } catch (error) {
      if (!isAxios404Error(error)) throw error;
      onNotFound?.();
      item = cachedItem ?? undefined;
    }
  }

  const declared = isOperationDeclaredOnItem(item, options);

  if (declared === true) return;

  if (declared === false || !accountAllowsOperation(queryClient, options)) {
    queryClient.removeQueries({ queryKey: options.queryKey });
    throw redirect({ to: redirectTo, replace: true });
  }
}

export function findCachedListItem<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  readItems: (data: unknown) => readonly T[] | undefined,
  match: (item: T) => boolean
): (T & WithLinks) | undefined {
  for (const [, data] of queryClient.getQueriesData({ queryKey })) {
    const found = readItems(data)?.find(match);
    if (found) return found as T & WithLinks;
  }
  return undefined;
}

/** Cold account cache fails open, matching `requireOperation`. */
function accountAllowsOperation(queryClient: QueryClient, options: OptionsWithMeta) {
  if (!hasAccountDataInCache(queryClient)) return true;

  return isOperationAvailableInCache(queryClient, [options]);
}
